import Text from 'components/Text/Text';
import { ArchivedLabel } from 'components/Select/ArchivedLabel';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Checkbox } from 'antd';
import { IGetCategoryPositionChains, UserRoles, EntityStatus } from 'types';
import { AppContext, IAppContext } from 'appState/appContext';
import { SHOW_ARCHIVED_RESTAURANTS } from 'appConstants';
import OutsideFormSelect from 'components/Select/OutsideFormSelect';
import { ISelectOption } from 'components/Select/types';
import SearchField from './SearchField';
import {
	ChangeCategoryPositionContext,
	IChangeCategoryPositionContext,
	TChangeCategoryPositionSelectedRestaurant,
} from '../../../../../changeCategoryPositionState/changeCategoryPositionContext';
import { useFetchData } from 'hooks/useFetchData';
import { useRoleAccess } from 'hooks/useRoleAccess';
import { getCategoryPositionChains } from 'backend/categories';
import style from './RestaurantMultipleSelect.module.scss';

export const RestaurantMultipleSelect: React.FC = () => {
	const { loggedUser } = useContext<IAppContext>(AppContext);
	const {
		setAdditionalRestaurants,
		selectedRestaurant,
		selectedAdditionalRestaurants,
		isApplyToOne,
		selectSearchString,
	} = useContext<IChangeCategoryPositionContext>(ChangeCategoryPositionContext);
	const hasAccess = useRoleAccess([
		UserRoles.SUPER_USER,
		UserRoles.CLIENT_MANAGER,
		UserRoles.GROUP_MANAGER,
		UserRoles.TECHNICAL,
	]);
	const { response, loading, refetch, responseError } = useFetchData<IGetCategoryPositionChains, string>(
		getCategoryPositionChains,
		selectedRestaurant?.id || '',
	);
	const [isSelectedAllRestaurants, setIsSelectedAllRestaurants] = useState<boolean>(false);
	const [showArchived, setShowArchived] = useState<boolean>(
		localStorage.getItem(SHOW_ARCHIVED_RESTAURANTS) === 'true',
	);

	const selectValue = selectedAdditionalRestaurants.map((el) => el.id);

	const accessibleRestaurants = useMemo(() => {
		const accessibleItems = loggedUser.restaurants || [];
		const restaurantsFromServer = response?.data.restaurants.filter((el) => el.id !== selectedRestaurant?.id) || [];
		const intersectedRestaurants = restaurantsFromServer.filter((el) =>
			accessibleItems.some((item) => item.id === el.id),
		);
		return hasAccess ? restaurantsFromServer : intersectedRestaurants;
	}, [response, selectedRestaurant, loggedUser, hasAccess]);

	const additionalRestaurants = useMemo(() => {
		const selectedIds = selectedAdditionalRestaurants.map((el) => el.id);
		return showArchived
			? accessibleRestaurants
			: accessibleRestaurants.filter((el) => el.status === EntityStatus.Active || selectedIds.includes(el.id));
	}, [accessibleRestaurants, showArchived, selectedAdditionalRestaurants]);

	const filteredRestaurants = useMemo(() => {
		return selectSearchString
			? additionalRestaurants.filter((el) => el.name.toLowerCase().includes(selectSearchString.toLowerCase()))
			: additionalRestaurants;
	}, [selectSearchString, additionalRestaurants]);

	const mappedOptions = useMemo<ISelectOption[]>(() => {
		return filteredRestaurants.map((el) => ({
			id: el.id,
			name: el.name,
			additionalContent: el.status === EntityStatus.Archived ? <ArchivedLabel /> : undefined,
		}));
	}, [filteredRestaurants]);

	useEffect(() => {
		if (selectedAdditionalRestaurants.length === 0) {
			// To turn off checkbox when remove all selected restaurants from list
			setIsSelectedAllRestaurants(false);
		} else if (selectedAdditionalRestaurants.length === additionalRestaurants.length) {
			// To turn on checkbox when select all restaurants in list
			setIsSelectedAllRestaurants(true);
		} else if (selectedAdditionalRestaurants.length !== additionalRestaurants.length) {
			// To turn off checkbox when not all restaurants are selected from the list
			setIsSelectedAllRestaurants(false);
		}
	}, [selectedAdditionalRestaurants.length, additionalRestaurants.length]);

	const handleSelectAllRestaurants = () => {
		setIsSelectedAllRestaurants((prevState) => {
			prevState ? setAdditionalRestaurants([]) : setAdditionalRestaurants(additionalRestaurants);
			return !prevState;
		});
	};

	const toggleShowArchived = () => {
		localStorage.setItem(SHOW_ARCHIVED_RESTAURANTS, JSON.stringify(!showArchived));
		setShowArchived((prevValue) => !prevValue);
	};

	const handleChange = (ids: string[]) => {
		const selectedItems: TChangeCategoryPositionSelectedRestaurant[] = ids.map((id) => {
			const alreadySelected = selectedAdditionalRestaurants.find((item) => item.id === id);
			if (alreadySelected) {
				return alreadySelected;
			}
			return { id, name: additionalRestaurants.find((item) => item.id === id)?.name || '' };
		});
		setAdditionalRestaurants(selectedItems);
	};

	const renderSelectMenu = (menu: React.ReactElement) => {
		return (
			<>
				<div className={style.selectMenuHeader}>
					<Checkbox
						className={style.checkBox}
						checked={isSelectedAllRestaurants}
						disabled={!additionalRestaurants.length}
						onChange={handleSelectAllRestaurants}>
						<Text
							translationNameSpace="form"
							translationKey="selectAllRestaurantsLabel"
							asFragment={true}
						/>
					</Checkbox>
					<Checkbox className={style.checkBox} checked={showArchived} onChange={toggleShowArchived}>
						<Text translationNameSpace="modals" translationKey="showArchived" asFragment={true} />
					</Checkbox>
					<SearchField />
				</div>
				{menu}
			</>
		);
	};

	return (
		<OutsideFormSelect
			testId="RestaurantMultipleSelect"
			name="IDsSelectedAdditionalRestaurantsForChangeCategoryPosition"
			label="restaurantsLabel"
			value={selectValue}
			options={mappedOptions}
			fetchError={responseError}
			loading={loading}
			onRefetch={refetch}
			disabled={isApplyToOne || loading}
			onChange={handleChange}
			counterValue={selectedAdditionalRestaurants.length}
			withValidation={false}
			dropdownRender={renderSelectMenu}
			mode="multiple"
		/>
	);
};
